import { EyeOutlined } from '@ant-design/icons';
import { Button, Descriptions, Modal, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { isValidElement, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useTranslation from '../../localization/useTranslation';
import AttachmentsPreview from '../Common/AttachmentsPreview';

const ACTIONS_KEYS = new Set(['actions', 'action', 'properties_action']);
const TIMESTAMP_FIELDS = [
  ['created_at', 'common.createdAt'],
  ['updated_at', 'common.updatedAt'],
];

const isActionsColumn = (column) =>
  ACTIONS_KEYS.has(column?.key) || ACTIONS_KEYS.has(column?.dataIndex);

const flattenColumns = (columns) =>
  (Array.isArray(columns) ? columns : [])
    .filter(Boolean)
    .flatMap((col) => (Array.isArray(col.children) ? flattenColumns(col.children) : [col]));

const getValue = (record, dataIndex) => {
  if (dataIndex == null) return undefined;
  if (Array.isArray(dataIndex)) {
    return dataIndex.reduce((acc, key) => (acc == null ? acc : acc[key]), record);
  }
  return record?.[dataIndex];
};

const toDisplayNode = (node) => {
  if (node == null || node === '' || node === false) return '—';
  if (isValidElement(node) || Array.isArray(node)) return node;
  if (typeof node === 'object') {
    // Legacy antd cell shape: { children, props: { rowSpan } }
    return 'children' in node ? toDisplayNode(node.children) : '—';
  }
  return node;
};

const renderColumn = (column, record) => {
  const value = getValue(record, column.dataIndex);
  if (typeof column.render === 'function') {
    return toDisplayNode(column.render(value, record, 0));
  }
  return toDisplayNode(value);
};

const formatTimestamp = (value) => {
  const date = dayjs(value);
  return date.isValid() ? date.format('YYYY-MM-DD HH:mm') : '—';
};

export const useRecordViewModal = () => {
  const [viewRecord, setViewRecord] = useState(null);
  const openView = useCallback((record) => setViewRecord(record), []);
  const closeView = useCallback(() => setViewRecord(null), []);
  return { viewRecord, openView, closeView };
};

export const ViewRecordButton = ({ onClick, children, iconOnly = false, ...rest }) => {
  const { t } = useTranslation();
  if (iconOnly) {
    return (
      <Tooltip title={t('common.view')}>
        <Button type="text" size="small" icon={<EyeOutlined />} aria-label={t('common.view')} onClick={onClick} {...rest} />
      </Tooltip>
    );
  }
  return (
    <Button icon={<EyeOutlined />} onClick={onClick} {...rest}>
      {children ?? t('common.view')}
    </Button>
  );
};

/**
 * Read-only details modal for a list row. Renders every non-action table
 * column, then `extraItems`, then `record.attachments` (when it is an array
 * and no column shows it), then created/updated timestamps when the record
 * carries them and no column already shows them.
 */
const RecordViewModal = ({
  record,
  columns,
  title,
  extraItems,
  excludeKeys = [],
  detailsPath,
  onClose,
  width = 640,
}) => {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const open = record != null;

  const items = useMemo(() => {
    if (!record) return [];
    const excluded = new Set(excludeKeys);
    const flat = flattenColumns(columns).filter((col) => {
      if (isActionsColumn(col)) return false;
      const key = col.key ?? col.dataIndex;
      return !(key != null && excluded.has(key));
    });

    const fromColumns = flat.map((col, index) => ({
      key: String(col.key ?? (Array.isArray(col.dataIndex) ? col.dataIndex.join('.') : col.dataIndex) ?? index),
      label: typeof col.title === 'function' ? col.title({}) : col.title,
      children: renderColumn(col, record),
    }));

    const extra = (typeof extraItems === 'function' ? extraItems(record) : extraItems ?? [])
      .filter(Boolean)
      .map((item, index) => ({
        key: String(item.key ?? `extra-${index}`),
        label: item.label,
        children: toDisplayNode(item.children),
      }));

    const shown = new Set(flat.map((col) => col.dataIndex).filter((d) => typeof d === 'string'));
    extra.forEach((item) => shown.add(item.key));

    const attachmentItems = [];
    if (Array.isArray(record.attachments) && !shown.has('attachments') && !excluded.has('attachments')) {
      const hasAttachments = record.attachments.some((a) => a && !a.deleted_at);
      attachmentItems.push({
        key: 'attachments',
        label: t('common.attachments'),
        children: hasAttachments ? <AttachmentsPreview attachments={record.attachments} size={110} /> : '—',
      });
    }

    const timestamps = TIMESTAMP_FIELDS
      .filter(([field]) => record[field] && !shown.has(field))
      .map(([field, labelKey]) => ({
        key: field,
        label: t(labelKey),
        children: formatTimestamp(record[field]),
      }));

    return [...fromColumns, ...extra, ...attachmentItems, ...timestamps];
  }, [record, columns, extraItems, excludeKeys, t]);

  const resolvedPath = open
    ? (typeof detailsPath === 'function' ? detailsPath(record) : detailsPath)
    : null;
  const resolvedTitle = open
    ? (typeof title === 'function' ? title(record) : title) ?? t('common.details')
    : null;

  return (
    <Modal
      open={open}
      title={resolvedTitle}
      onCancel={onClose}
      width={width}
      destroyOnClose
      footer={[
        resolvedPath ? (
          <Button
            key="open"
            type="primary"
            onClick={() => {
              onClose?.();
              navigate(resolvedPath);
            }}
          >
            {t('common.openDetailsPage')}
          </Button>
        ) : null,
        <Button key="close" onClick={onClose}>
          {t('common.close')}
        </Button>,
      ]}
    >
      <Descriptions
        bordered
        column={1}
        size="small"
        labelStyle={{ width: '35%', whiteSpace: 'nowrap' }}
        dir={language === 'ar' ? 'rtl' : 'ltr'}
      >
        {items.map((item) => (
          <Descriptions.Item key={item.key} label={item.label}>
            {item.children}
          </Descriptions.Item>
        ))}
      </Descriptions>
    </Modal>
  );
};

export default RecordViewModal;
